import { useEffect, useRef } from 'react';
import { notify } from '../../../utils/notification';
import { hasSubclassFeature } from '../../../shared/rules/subclasses/index.mjs';
import { HUNTERS_LORE_FEATURE_ID } from '../../../shared/rules/subclasses/ranger/huntersPrey.mjs';
import { HUNTERS_MARK_EFFECT_ID } from '../classFeatures/ranger/huntersMark';

const list = (value) => (Array.isArray(value) ? value : String(value || '').split(','))
  .map((entry) => String(entry?.name || entry?.index || entry).trim()).filter(Boolean);

/** Hunter's Lore summary for a creature: its Immunities, Resistances and Vulnerabilities, or that it has none. */
export const describeHuntersLore = (entity = {}) => {
  const parts = [
    ['Immune', [...list(entity.damageImmunities), ...list(entity.conditionImmunities)]],
    ['Resistant', list(entity.damageResistances)],
    ['Vulnerable', list(entity.damageVulnerabilities)],
  ].filter(([, values]) => values.length).map(([label, values]) => `${label}: ${values.join(', ')}`);
  return parts.length ? parts.join(' · ') : 'No Immunities, Resistances or Vulnerabilities';
};

/** The creature this caster's Hunter's Mark is on, or null. */
export const markedTargetId = (activeEffects, casterId) => {
  const mark = (Array.isArray(activeEffects) ? activeEffects : []).find((effect) => effect?.definitionId === HUNTERS_MARK_EFFECT_ID
    && effect.parentEffectId && effect.targetCombatantId != null && String(effect.sourceCombatantId) === String(casterId));
  return mark ? String(mark.targetCombatantId) : null;
};

/**
 * Hunter's Lore (Hunter 3): when a Hunter marks a new creature, tell the player that creature's
 * Immunities, Resistances and Vulnerabilities. A mark already in place on load stays quiet.
 */
export default function useHuntersLoreNotice({ form, characterId, combatState, spatialContext }) {
  const seen = useRef(undefined);
  const targetId = hasSubclassFeature(form, HUNTERS_LORE_FEATURE_ID) ? markedTargetId(combatState?.activeEffects, characterId) : null;
  const entity = targetId ? spatialContext?.entities?.[targetId] : null;
  useEffect(() => {
    if (targetId && !entity) return; // Wait for the map's creature metadata before deciding.
    const first = seen.current === undefined;
    const changed = targetId !== seen.current;
    seen.current = targetId;
    if (first || !changed || !targetId) return;
    notify(`Hunter's Lore · ${entity.label || entity.name || 'Marked creature'}: ${describeHuntersLore(entity)}.`, 'info');
  }, [targetId, entity]);
}
